import { isEqual } from 'lodash';

import { DisplayElementModel } from './display-element.model';
import { BriefingImageDisplayElementApiModel } from '../../../api/briefings/model/briefing-image-display-element-api.model';
import { ImageStyleModel } from './image-style.model';
import { ImageHelper } from '../helpers/image.helper';
import { BriefingImagePropertiesModel } from './briefing-image-properties.model';
import { BriefingImageEffectsModel } from './briefing-image-effects.model';
import { BriefingImageFrameEnum } from '../../../Monitor/Shared/enums/briefing-image-frame.enum';

export class BriefingImageDisplayElementModel extends DisplayElementModel {
    styles: ImageStyleModel;
    imageProperties: BriefingImagePropertiesModel;
    images: BriefingImagePropertiesModel[];
    imageEffects: BriefingImageEffectsModel;

    isBoundToGlobalStyles: boolean = true;
    placeholderHeight: number = 120;

    constructor(
        apiModel: BriefingImageDisplayElementApiModel
    ) {
        super();

        if (apiModel) {
            this.styles = new ImageStyleModel(apiModel.styles);
            if (apiModel.imageProperties) {
                this.imageProperties = new BriefingImagePropertiesModel(apiModel.imageProperties);
            } else {
                this.imageProperties = new BriefingImagePropertiesModel();
            }
            if (apiModel.images?.length) {
                this.images = apiModel.images.map(i => new BriefingImagePropertiesModel(i));
                if (this.imageProperties.url && this.images.length === 1) {
                    this.imageProperties.url = this.images[0].url;
                    this.imageProperties.width = this.images[0].width;
                    this.imageProperties.imageSource = this.images[0].imageSource;
                }
            }
            if (apiModel.imageEffects) {
                this.imageEffects = new BriefingImageEffectsModel(apiModel.imageEffects);
            } else {
                this.imageEffects = new BriefingImageEffectsModel();
            }

            this.automaticallyGenerated = apiModel.automaticallyGenerated;
        } else {
            this.styles = new ImageStyleModel();
            this.images = [];
            this.imageProperties = new BriefingImagePropertiesModel();
            this.imageEffects = new BriefingImageEffectsModel();
        }
    }

    canRender(): boolean {
        return (this.imageEffects?.imageFrame === BriefingImageFrameEnum.COLLAGE) || (this.images?.length > 0);
    }

    toApi(width?: number): BriefingImageDisplayElementApiModel {
        const apiModel =  <BriefingImageDisplayElementApiModel> {
            styles: JSON.parse(JSON.stringify(this.styles)),
            imageProperties: this.imageProperties?.toApi(width),
            imageEffects: this.imageEffects?.toApi(),
            images: this.images?.map(image => image.toApi(width)),
        };

        if (apiModel.imageProperties && this.images?.length === 1) {
            apiModel.imageProperties.url = this.images[0].url;
            apiModel.imageProperties.width = this.images[0].width;
            apiModel.imageProperties.imageSource = this.images[0].imageSource;
        }

        apiModel.automaticallyGenerated = this.automaticallyGenerated;

        return apiModel;
    }

    clone(): BriefingImageDisplayElementModel {
        const clone = new BriefingImageDisplayElementModel(this.toApi()) as this;
        clone.displayType = this.displayType;

        return clone;
    }

    async onUpdate(): Promise<boolean> {
        let changes = false;
        if (this.images?.length) {
            for (const image of this.images) {
                if (image.url !== image.oldUrl) {
                    changes = true;

                    if (image.url) {
                        try {
                            const width = await ImageHelper.getImageWidth(image.url);

                            if (image.width) {
                                image.width = Math.min(width, image.width, 640);
                            } else {
                                image.width = Math.min(width, 640);
                            }

                        } catch (e) {
                            // Probably couldn't fetch the image
                            if (!image.width) {
                                image.width = 640;
                            }
                        }
                    } else {
                        if (!image.width) {
                            image.width = 640;
                        }
                    }
                }
                image.oldUrl = image.url;
            }
        }
        return changes;
    }

    isEqual(other: BriefingImageDisplayElementModel): boolean {
        if (!this.styles.equals(other.styles)) {
            return false;
        }
        if (!isEqual(this.imageEffects, other.imageEffects)) {
            return false;
        }
        if ((this.imageProperties || other.imageProperties) && !this.imageProperties?.equals(other.imageProperties)) {
            return false;
        }

        if (this.images?.length !== other.images?.length) {
            return false;
        }
        if (this.images?.some((img, i) => !img.equals(other.images[i]))) {
            return false;
        }

        if (!isEqual(this.automaticallyGenerated, other.automaticallyGenerated)) {
            return false;
        }
        return true;
    }
}
